import { ActionIcon, Button, Select, TextInput } from '@mantine/core';
import { IconCalendar, IconEye, IconPlus, IconPointFilled, IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import orders from './Orders.module.css';
import {
  filterPurchaseOrders,
  orderWarnings,
  PO_NO_FILTER,
  PO_STATUSES,
  PO_TONE,
  type PurchaseOrderFilter,
} from './orders.js';
import classes from './Purchasing.module.css';
import { formatAmount, sortItems } from './requisitions.js';
import type { PurchaseOrderSource } from './source.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from './table.js';
import type {
  PurchaseOrderList as List,
  PurchaseOrderStatus,
  PurchaseOrderSummary,
} from './types.js';

type Column = 'poNo' | 'date' | 'supplier' | 'vessel' | 'amount' | 'status';

const SORT_VALUE: Record<Column, (item: PurchaseOrderSummary) => string | number | null> = {
  poNo: (item) => item.poNo,
  date: (item) => item.orderDate,
  supplier: (item) => item.supplierName,
  vessel: (item) => item.vesselName,
  amount: (item) => item.total,
  status: (item) => item.status,
};

const PAGE_SIZE = 10;

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: List; vessels: PersonnelVessel[] };

export function PurchaseOrderStatusBadge({ status }: { status: PurchaseOrderStatus }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={PO_TONE[status]} icon={<IconPointFilled size={10} />}>
      {t(`purchasing.po.statuses.${status}`)}
    </StatusBadge>
  );
}

/**
 * Sipariş listesi (`gemi-satin-alma.jpeg` "Purchase Order (PO)"): arama, Vessel, Status ve tarih
 * aralığı süzgeçleri; PO No / Date / Supplier / Vessel / Amount / Status / Actions tablosu ve
 * sayfalama. Ofiste "New PO" (seçilmiş RFQ'dan); gemide salt okunur (ADR 0026 §1).
 */
export function PurchaseOrderList({
  source,
  onOpen,
  onNew,
}: {
  source: PurchaseOrderSource;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.po.list.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [filter, setFilter] = useState<PurchaseOrderFilter>(PO_NO_FILTER);
  const [page, setPage] = useState(1);
  // Varsayılan sıra sunucununki gibi: yeniden eskiye (numara)
  const [sort, setSort] = useState<SortState<Column>>({ key: 'poNo', dir: 'desc' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const [list, vessels] = await Promise.all([source.list(), source.vessels()]);
      setState({ kind: 'ready', list, vessels });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const items = useMemo(
    () =>
      ready
        ? sortItems(filterPurchaseOrders(ready.list.items, filter), sort.dir, SORT_VALUE[sort.key])
        : [],
    [ready, filter, sort],
  );
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const change = (next: Partial<PurchaseOrderFilter>) => {
    setFilter((f) => ({ ...f, ...next }));
    setPage(1);
  };
  const ship = source.scope === 'ship';
  // PO ofiste açılır (ADR 0026 §1): gemide düğme yok
  const canCreate = Boolean(ready?.list.canCreate && source.editor);

  return (
    <div className={classes.screen}>
      <PageHeader title={k('title')} description={k(ship ? 'descriptionShip' : 'descriptionWeb')} />

      <Panel flush aria-label={k('table')}>
        <div className={classes.toolbar} data-single={canCreate ? undefined : true}>
          {canCreate ? (
            <Button leftSection={<IconPlus size={16} />} onClick={onNew}>
              {k('new')}
            </Button>
          ) : null}
          <form
            className={orders.filters}
            aria-label={k('filters')}
            onSubmit={(event) => event.preventDefault()}
          >
            <TextInput
              label={k('search')}
              placeholder={k('searchPlaceholder')}
              leftSection={<IconSearch size={16} aria-hidden />}
              value={filter.search}
              onChange={(event) => change({ search: event.currentTarget.value })}
            />
            <Select
              label={k('vessel')}
              data={[
                { value: '', label: k('allVessels') },
                ...(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name })),
              ]}
              value={ship ? (ready?.vessels[0]?.id ?? '') : (filter.vesselId ?? '')}
              allowDeselect={false}
              readOnly={ship}
              // Gemide salt okunur seçici: ok yok (M6f §0.6)
              {...(ship ? { rightSection: null } : {})}
              onChange={(value) => change({ vesselId: value || null })}
            />
            <Select
              label={k('status')}
              data={[
                { value: '', label: k('allStatuses') },
                ...PO_STATUSES.map((value) => ({
                  value,
                  label: t(`purchasing.po.statuses.${value}`),
                })),
              ]}
              value={filter.status ?? ''}
              allowDeselect={false}
              onChange={(value) =>
                change({ status: (value || null) as PurchaseOrderStatus | null })
              }
            />
            <fieldset className={classes.range}>
              <legend className={classes.label}>{k('dateRange')}</legend>
              <div className={classes.rangeBox}>
                <IconCalendar size={16} aria-hidden />
                <DayInput
                  variant="unstyled"
                  size="xs"
                  rightSection={null}
                  aria-label={k('dateFrom')}
                  value={filter.from}
                  maxDate={filter.to ?? undefined}
                  onChange={(value) => change({ from: value })}
                />
                <span aria-hidden>–</span>
                <DayInput
                  variant="unstyled"
                  size="xs"
                  rightSection={null}
                  aria-label={k('dateTo')}
                  value={filter.to}
                  minDate={filter.from ?? undefined}
                  onChange={(value) => change({ to: value })}
                />
              </div>
            </fieldset>
          </form>
        </div>
        {state.kind === 'error' ? (
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={7} label={k('table')} />
        ) : (ready?.list.items.length ?? 0) === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k(ship ? 'emptyShip' : 'emptyWeb')} />
        ) : items.length === 0 ? (
          <NoMatch
            title={k('noMatch')}
            onClear={() => {
              setFilter(PO_NO_FILTER);
              setPage(1);
            }}
          />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    {(Object.keys(SORT_VALUE) as Column[]).map((column) => (
                      <SortableTh
                        key={column}
                        column={column}
                        label={k(`columns.${column}`)}
                        sort={sort}
                        onSort={setSort}
                        numeric={column === 'amount'}
                        {...(column === 'poNo' ? { className: classes.key } : {})}
                      />
                    ))}
                    <th scope="col" data-numeric>
                      {k('columns.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.id}>
                      <td className={classes.key}>
                        <a
                          className={classes.link}
                          href={`?id=${item.id}`}
                          aria-label={k('open', { no: item.poNo })}
                          onClick={(event) => {
                            event.preventDefault();
                            onOpen(item.id);
                          }}
                        >
                          {item.poNo}
                        </a>
                      </td>
                      <td>{formatDay(item.orderDate)}</td>
                      <td>{item.supplierName}</td>
                      <td>{item.vesselName}</td>
                      <td data-numeric className={classes.nowrap}>
                        {`${formatAmount(item.total, locale)} ${item.currency}`}
                      </td>
                      <td>
                        <span className={classes.flags}>
                          <PurchaseOrderStatusBadge status={item.status} />
                          {orderWarnings(item).map((warning) => (
                            <StatusBadge key={warning} tone="danger">
                              {k(warning)}
                            </StatusBadge>
                          ))}
                        </span>
                      </td>
                      <td data-numeric>
                        <ActionIcon
                          variant="subtle"
                          aria-label={k('open', { no: item.poNo })}
                          onClick={() => onOpen(item.id)}
                        >
                          <IconEye size={18} />
                        </ActionIcon>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </div>
  );
}
